import React, { useState, useMemo, useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import CreatableSelect from "react-select/creatable";
import ReactFlagsSelect from "react-flags-select";
import Select from "react-select";
import { useFormik } from "formik";
import * as Yup from "yup";
import {
  submitJob,
  fetchAllSkills,
  createSkill,
  fetchJobById,
  createJobAssessment,
  extractJobDetailsFromDoc,
} from "../../Redux/ActionCreators/jobsAction";
import countriesData from "../../utils/countries.json";
import citiesData from "../../utils/cities.json";
import { Snackbar, Alert } from "@mui/material";
import "./jobs.scss";

const countriesList =
  countriesData[
    "select id, name, iso3, numeric_code, iso2, emoji  from countries where flag =1"
  ];
const citiesList =
  citiesData["select id, name, country_id from cities where flag = 1"];

const CreateJob = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const fileInputRef = useRef();

  const [submitType, setSubmitType] = useState("draft");
  const [notification, setNotification] = useState("");
  const [isHover, setIsHover] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [generating, setGenerating] = useState(false);

  const allSkills = useSelector((state) => state.jobs.skills);
  const [skillOptions, setSkillOptions] = useState([]);

  useEffect(() => {
    dispatch(fetchAllSkills());
  }, [dispatch]);

  useEffect(() => {
    setSkillOptions(allSkills.map((s) => ({ value: s.name, label: s.name })));
  }, [allSkills]);

  const validationSchema = Yup.object().shape({
    title: Yup.string().required("Job title is required"),
    company_name: Yup.string().required("Company name is required"),
    job_type: Yup.string().required("Job type is required"),
    country: Yup.string().required("Country is required"),
    city: Yup.string().required("City is required"),
    experience_required: Yup.string().required("Experience is required"),
    qualifications: Yup.string(),
    skills: Yup.array().min(1, "At least one skill is required"),
    description: Yup.string().required("Description is required"),
  });

  const jobTypeIdMap = {
    "Part Time": "64b3c119-597e-4f8a-a66a-b448fc2baf44",
    "Full Time": "9c4ae6ff-a326-4ddb-936e-042cdd715bc2",
  };

  const formik = useFormik({
    initialValues: {
      title: "",
      company_name: "",
      job_type: "",
      skills: [],
      country: "US",
      city: "",
      experience_required: "",
      salary: "",
      description: "",
      qualifications: "",
      additional_input: "",
      autoGenerate: false
    },
    validationSchema,
    onSubmit: async (values, { setSubmitting }) => {
      setSubmitting(true);
      const selectedCountry = countriesList.find(
        (c) => c.iso2 === values.country
      );
      const selectedCity = citiesList.find(
        (city) =>
          city.name === values.city && city.country_id === selectedCountry?.id
      );
      const skillIds = values.skills
        .map((name) => allSkills.find((s) => s.name === name)?.id)
        .filter(Boolean);

      const payload = {
        experience_required: values.experience_required,
        salary: values.salary,
        qualifications: values.qualifications,
        job_type_id: jobTypeIdMap[values.job_type],
        job_type: values.job_type,
        country_id: selectedCountry?.id || null,
        city_id: selectedCity?.id || null,
        country: selectedCountry?.name || values.country,
        city: selectedCity?.name || values.city,
        description: values.description,
        skills: values.skills,
        skill_ids: skillIds,
        is_active: true,
        url: `https://demo.gappeo.ai/jobs/[job_id]/apply`,
        job_status_id:
          submitType === "draft"
            ? "855705f5-9696-46fc-b402-4a18ffd819af"
            : "14107328-840b-4ad0-904f-8a2243720f70",
        company_name: values.company_name,
        title: values.title,
        additional_input: { voice_call_description: values.additional_input },
      };

      const response = await dispatch(submitJob(payload));
      if (typeof response === "string") {
        const job = await dispatch(fetchJobById(response));

        const isAssessmentNeeded = job?.id && !job.assessment_id;
        const title = job?.title ?? formik.values.title;
        const description = job?.description ?? formik.values.description;

        if (isAssessmentNeeded && skillIds.length > 0 && title && description) {
          const generateUniqueCode = () => Math.random().toString(36).substring(2, 8);
          const formattedTitle = `${title} - ${generateUniqueCode()}`;
          // await dispatch(
          //   createJobAssessment(job.id, skillIds, formattedTitle, description)
          // );
        }
        setSnackbar({ open: true, message: "Job created successfully!", severity: "success" });
        if (!formik.values.autoGenerate) {
          setTimeout(() => {
            navigate("/jobs", {
              state: {
                defaultTab: submitType === "draft" ? "Draft" : "Published",
              },
            });
          }, 1000);
        }
      } else {
        setSnackbar({ open: true, message: "Job creation failed.", severity: "error" });
      }

      setSubmitting(false);
    },
  });
  

  const countryOptions = useMemo(() => {
    return countriesList.map((c) => ({
      label: `${c.emoji} ${c.iso3}`,
      value: c.iso2,
      id: c.id,
      iso3: c.iso3,
    }));
  }, []);

  const cityOptions = useMemo(() => {
    const selectedCountry = countriesList.find(
      (c) => c.iso2 === formik.values.country
    );
    if (!selectedCountry) return [];
    return citiesList
      .filter((city) => city.country_id === selectedCountry.id)
      .map((city) => ({
        label: city.name,
        value: city.name,
        id: city.id,
      }));
  }, [formik.values.country]);

  const saveDraftAndGenerateAI = async () => {
    setSubmitType("draft");
    await formik.setFieldValue("autoGenerate", true);
    const errors = await formik.validateForm();
    if (Object.keys(errors).length > 0) {
      formik.setTouched(errors);
      setSnackbar({ open: true, message: "Please fill all required fields", severity: "warning" });
      return;
    }
    setGenerating(true)
    const selectedCountry = countriesList.find((c) => c.iso2 === formik.values.country);
    const selectedCity = citiesList.find((city) => city.name === formik.values.city && city.country_id === selectedCountry?.id);
    const skillIds = formik.values.skills.map((name) => allSkills.find((s) => s.name === name)?.id).filter(Boolean);

    const payload = {
      experience_required: formik.values.experience_required,
      salary: formik.values.salary,
      qualifications: formik.values.qualifications,
      job_type_id: jobTypeIdMap[formik.values.job_type],
      job_type: formik.values.job_type,
      country_id: selectedCountry?.id || null,
      city_id: selectedCity?.id || null,
      country: selectedCountry?.name || formik.values.country,
      city: selectedCity?.name || formik.values.city,
      description: formik.values.description,
      skills: formik.values.skills,
      skill_ids: skillIds,
      is_active: true,
      url: `https://demo.gappeo.ai/jobs/[job_id]/apply`,
      job_status_id: "855705f5-9696-46fc-b402-4a18ffd819af",
      company_name: formik.values.company_name,
      title: formik.values.title,
      additional_input: { voice_call_description: formik.values.additional_input },
    };

    const jobId = await dispatch(submitJob(payload));
    if (typeof jobId === "string") {
      const job = await dispatch(fetchJobById(jobId));

      const isAssessmentNeeded = job?.id && !job.assessment_id;
      const title = job?.title ?? formik.values.title;
      const description = job?.description ?? formik.values.description;

      if (isAssessmentNeeded && skillIds.length > 0 && title && description) {
        const generateUniqueCode = () => Math.random().toString(36).substring(2, 8);
        const formattedTitle = `${title} - ${generateUniqueCode()}`;
        // await dispatch(
        //   createJobAssessment(job.id, skillIds, formattedTitle, description)
        // );
      }

      navigate("/jobs/editJob", { state: { jobId, autoGenerate: true } });
    } else {
      setSnackbar({ open: true, message: "Job draft save failed", severity: "error" });
    }
    setGenerating(false)
  };

  const isBusy = formik.isSubmitting || generating;

  const handleFileChange = (e) => {
    setSelectedFile(e.target.files[0]);
  };

  const handleUploadSubmit = async () => {
    if (!selectedFile) {
      return;
    }

    setUploading(true);

    const formData = new FormData();
    formData.append("file", selectedFile);

    const response = await dispatch(extractJobDetailsFromDoc(formData));

    if (response) {
      const {
        job_title,
        company_name,
        job_type,
        skills_required,
        job_location_text,
        job_location_iso,
        experience_required,
        job_salary,
        job_qualification,
        job_description,
      } = response;

      const jobTypeFormatted = job_type === "Full-time" ? "Full Time" : job_type;

      const existingSkillNames = allSkills.map((s) => s.name);
      const matchedSkills = (skills_required || []).filter((skill) => existingSkillNames.includes(skill));
      await formik.setFieldValue("skills", matchedSkills);

      // Set all form fields
      await formik.setFieldValue("title", job_title || "");
      await formik.setFieldValue("company_name", company_name || "");
      await formik.setFieldValue("job_type", jobTypeFormatted || "");
      await formik.setFieldValue("skills", skills_required || []);
      await formik.setFieldValue("country", job_location_iso || "US");
      await formik.setFieldValue("city", job_location_text?.split(",")[0].trim() || "");
      await formik.setFieldValue("experience_required", experience_required || "");
      await formik.setFieldValue("salary", job_salary || "");
      await formik.setFieldValue("qualifications", job_qualification || "");
      await formik.setFieldValue("description", job_description || "");


      // Reset and close modal
      setTimeout(() => {
        setShowUploadModal(false);
        setSelectedFile(null);
        if (fileInputRef.current) fileInputRef.current.value = null;
        setSnackbar({
          open: true,
          message: "Job details filled successfully",
          severity: "success",
        });
      }, 300);
    }

    setUploading(false);
  };

  return (
    <div className="create-job-page">
      <div className="create-job-head">
        <h1>
          <img
            src="/images/leftarrow-black-icon.svg"
            alt=""
            onClick={() => navigate("/jobs")}
          />
          Create a Job
        </h1>
        <div className="button-group">
          <button className="upload-btn" onClick={() => setShowUploadModal(true)} onMouseEnter={() => {setIsHover(true)}} onMouseLeave={() => {setIsHover(false)}}>
            <img src={isHover ? "/images/pencil-alt-icon-wight.svg" : "/images/pencil-alt-icon-black.svg"} alt="" width={16} height={16} />
            &nbsp;Upload document
          </button>
        </div>
      </div>

      <form onSubmit={formik.handleSubmit}>
        <div className="create-job-body">
          <div className="row">
            <div className="col-md-5">
              <div className="form-section">
                <label>Job title <span style={{color:'red'}}>*</span></label>
                <input
                  type="text"
                  name="title"
                  placeholder="Enter job title"
                  value={formik.values.title}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  className={formik.touched.title && formik.errors.title ? "input-error" : ""}
                  disabled={isBusy}
                />
              </div>
            </div>
            <div className="col-md-5">
              <div className="form-section">
                <label>Company name <span style={{color:'red'}}>*</span></label>
                <input
                  type="text"
                  name="company_name"
                  placeholder="Enter company name"
                  value={formik.values.company_name}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  className={formik.touched.company_name && formik.errors.company_name ? "input-error" : ""}
                  disabled={isBusy}
                />
              </div>
            </div>
            <div className="col-md-5">
              <div className="form-section">
                <label>Job type <span style={{color:'red'}}>*</span></label>
                <select
                  className={formik.touched.job_type && formik.errors.job_type ? "input-error form-control mt-2" : "form-control mt-2"}
                  name="job_type"
                  value={formik.values.job_type}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  disabled={isBusy}
                >
                  <option hidden>Select job type</option>
                  <option value="Full Time">Full Time</option>
                  <option value="Part Time">Part Time</option>
                </select>
              </div>
            </div>
            <div className="col-md-5">
              <div className="form-section">
                <label>Skills required <span style={{color:'red'}}>*</span></label>
                <CreatableSelect
                  isMulti
                  className={formik.touched.skills && formik.errors.skills ? "input-error select mt-2" : "select mt-2"}
                  classNamePrefix="react-select"
                  name="skills"
                  placeholder="Select or type to add"
                  options={skillOptions}
                  menuPortalTarget={document.body}
                  styles={{
                    menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                  }}
                  onChange={async (selectedOptions) => {
                    const selectedNames = selectedOptions.map(
                      (opt) => opt.value
                    );
                    formik.setFieldValue("skills", selectedNames);

                    const existingNames = allSkills.map((s) => s.name);
                    const newSkills = selectedNames.filter(
                      (name) => !existingNames.includes(name)
                    );

                    for (const skillName of newSkills) {
                      const result = await dispatch(createSkill(skillName));
                      if (result?.id) {
                        // Optionally show a toast/snackbar here
                      }
                    }
                  }}
                  onBlur={() => formik.setFieldTouched("skills", true)}
                  value={formik.values.skills.map((skill) => ({
                    value: skill,
                    label: skill,
                  }))}
                  isDisabled={isBusy}
                />
              </div>
            </div>
            <div className="col-md-5">
              <div className="form-section">
                <label>Job country <span style={{color:'red'}}>*</span></label>
                <div className="location-selects d-flex">
                  <ReactFlagsSelect
                    selected={formik.values.country}
                    onSelect={(code) => {
                      formik.setFieldValue("country", code);
                      formik.setFieldValue("city", "");
                    }}
                    countries={countryOptions.map((c) => c.value)}
                    customLabels={Object.fromEntries(
                      countryOptions.map((c) => [c.name, `${c.iso3}`])
                    )}
                    placeholder="Country"
                    className={formik.touched.country && formik.errors.country ? "input-error country-flag-select" : "country-flag-select"}
                    selectable={!isBusy}
                    searchable={true}
                  />
                </div>
              </div>
            </div>
            <div className="col-md-5">
              <div className="form-section">
                <label>Job city <span style={{color:'red'}}>*</span></label>
                <div className="location-selects d-flex">
                  <Select
                    options={cityOptions}
                    isDisabled={isBusy}
                    placeholder="Select city"
                    className={formik.touched.city && formik.errors.city ? "input-error city-select" : "city-select"}
                    classNamePrefix="react-select"
                    name="city"
                    value={
                      cityOptions.find((o) => o.value === formik.values.city) ||
                      null
                    }
                    onChange={(option) =>
                      formik.setFieldValue("city", option ? option.value : "")
                    }
                    onBlur={() => formik.setFieldTouched("city", true)}
                  />
                </div>
              </div>
            </div>
            <div className="col-md-5">
              <div className="form-section">
                <label>Experience required <span style={{color:'red'}}>*</span></label>
                <input
                  type="text"
                  name="experience_required"
                  placeholder="Enter experience required"
                  value={formik.values.experience_required}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  className={formik.touched.experience_required && formik.errors.experience_required ? "input-error" : ""}
                  disabled={isBusy}
                />
              </div>
            </div>
            <div className="col-md-5">
              <div className="form-section">
                <label>Job salary</label>
                <input
                  name="salary"
                  type="text"
                  placeholder="Enter salary range"
                  value={formik.values.salary}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  disabled={isBusy}
                />
              </div>
            </div>
            <div className="col-md-5">
              <div className="form-section">
                <label>Job qualifications</label>
                <input
                  name="qualifications"
                  type="text"
                  placeholder="Enter job qualifications"
                  value={formik.values.qualifications}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  disabled={isBusy}
                />
              </div>
            </div>
            <div className="col-md-10">
              <div className="form-section text-area">
                <label>
                  <div className="d-flex justify-content-between">
                    <h6>Job description <span style={{color:'red'}}>*</span></h6>
                  </div>
                  <textarea
                    name="description"
                    placeholder="Write text here ..."
                    value={formik.values.description || ""}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    className={formik.touched.description && formik.errors.description ? "input-error" : ""}
                    disabled={isBusy}
                  />
                  <div className="ai-generate">
                    <div className="ai-generate-wrapper">
                      <button type="button" className="ai-generate-btn" onClick={saveDraftAndGenerateAI} disabled={generating}>
                        <img src="/images/ai-icon.png" alt="" />
                        &nbsp;{generating === true ? "Generating..." : "Generate with AI"}
                      </button>
                    </div>
                  </div>
                  {notification && (
                    <div className="text-danger">{notification}</div>
                  )}
                </label>
              </div>
              <div className="job-form-inputs d-flex mt-2"></div>
            </div>
          </div>
          <div className="button-group">
            <button
              disabled={isBusy}
              onClick={() => {setSubmitType("draft")}}
              onMouseEnter={() => {setIsHover(true)}} onMouseLeave={() => {setIsHover(false)}}
            >
              {formik.isSubmitting && submitType === "draft" ? (
                <>
                <img src={isHover ? "/images/pencil-alt-icon-wight.svg" : "/images/pencil-alt-icon-black.svg"} alt="" width={16} height={16} />
                  &nbsp;Saving...
                </>
              ) : (
                <>
                <img src={isHover ? "/images/pencil-alt-icon-wight.svg" : "/images/pencil-alt-icon-black.svg"} alt="" width={16} height={16} />
                  &nbsp;Save as Draft
                </>
              )}
            </button>
            <button
              className="primary"
              disabled={isBusy}
              onClick={() => {
                setSubmitType("publish");
              }}
            >
              {formik.isSubmitting && submitType === "publish" ? (
                <>
                  <img src="/images/check.svg" alt="" width={16} height={16} />
                  &nbsp;Publishing...
                </>
              ) : (
                <>
                  <img src="/images/check.svg" alt="" width={16} height={16} />
                  &nbsp;Publish Now
                </>
              )}
            </button>
          </div>
        </div>
      </form>
      {showUploadModal && (
          <div className="upload-modal">
            <div className="modal-content">
              <div className="modal-head">
                <div></div>
                <h3>Upload Job Details</h3>
                <img
                  src="/images/close-icon.svg"
                  alt="Close"
                  onClick={() => setShowUploadModal(false)}
                  style={{ cursor: "pointer" }}
                />
              </div>

              <div className="modal-input"
              style={{
                height: "280px",
                overflowY: "auto",
                scrollbarWidth: "thin",
                justifyContent: "unset",
              }}
              >
                <input
                  type="file"
                  accept=".doc,.docx"
                  ref={fileInputRef}
                  onChange={handleFileChange}
                  style={{ display: "none" }}
                />
                  <img src="/images/upload-icon-pink.svg" alt="upload" />
                  <p><strong style={{ fontWeight: 600 }}>Click to upload</strong></p>
                  <p>Supported format: .docx</p>
                  <p>Only one .docx file can be selected at a time.</p>
                  <div style={{ display: "flex", justifyContent: "center", gap: "16px" }}>
                    <button
                      className="upload-btn"
                      onClick={() => fileInputRef.current.click()}
                      disabled={uploading}
                    >
                      Browse .docx
                    </button>
                  </div>

                {selectedFile && (
                  <div style={{ marginTop: "0.5rem", marginBottom:"0.5rem", textAlign: "center" }}>
                    <p>
                      Selected File: <br /><strong>{selectedFile.name}</strong>
                    </p>
                  </div>
                )}
              </div>

              <div className="d-flex justify-content-center" style={{ marginTop: "1rem" }}>
                <button
                  className="upload-btn"
                  onClick={handleUploadSubmit}
                  disabled={uploading}
                >
                  {uploading ? "Uploading..." : "Upload Job Details"}
                </button>
              </div>
            </div>
          </div>
        )}
      <Snackbar open={snackbar.open} autoHideDuration={3000} onClose={() => setSnackbar({ ...snackbar, open: false })}>
        <Alert severity={snackbar.severity}>{snackbar.message}</Alert>
      </Snackbar>
    </div>
  );
};

export default CreateJob;
